import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bell, Check, Volume2, VolumeX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, timeAgo } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { armAudio, isSoundOn, setSoundOn, playNotificationChime, initSoundPreference } from "../lib/sound";

const POLL_MS = 25000;
// Kinds that earn a sound when newly received. Status updates, vendor flow,
// dashboard refreshes etc. are intentionally NOT in this list.
const SOUND_KINDS = new Set(["request_raised", "payment_completed"]);

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soundOn, setSoundOnState] = useState(isSoundOn());
  const navigate = useNavigate();
  const ref = useRef(null);
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  // Default sound ON for admin/accounts, OFF otherwise. Only seeded the FIRST
  // time we see the user — after that, user's choice wins.
  useEffect(() => {
    if (!user || initializedRef.current) return;
    initializedRef.current = true;
    const defaultOn = user.role === "admin" || user.role === "accounts";
    initSoundPreference(defaultOn);
    setSoundOnState(isSoundOn());
    armAudio();
  }, [user]);

  const fetchFeed = useCallback(async () => {
    try {
      const r = await api.get("/in-app-notifications", { params: { limit: 20 } });
      const newItems = r.data?.items || [];
      const newUnread = r.data?.unread_count || 0;

      // Detect freshly-received qualifying notifications and play chime ONCE.
      // First poll just seeds the seen set — never chimes for backlog.
      if (seenIdsRef.current.size === 0) {
        newItems.forEach((n) => seenIdsRef.current.add(n.id));
      } else {
        const fresh = newItems.filter((n) => !seenIdsRef.current.has(n.id) && !n.read);
        const qualifies = fresh.some((n) => SOUND_KINDS.has(n.kind) || n.emergency);
        if (qualifies) {
          playNotificationChime();
        }
        fresh.forEach((n) => seenIdsRef.current.add(n.id));
      }

      setItems(newItems);
      setUnread(newUnread);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, POLL_MS);
    return () => clearInterval(id);
  }, [fetchFeed]);

  useEffect(() => { if (open) fetchFeed(); }, [open, fetchFeed]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.post("/in-app-notifications/read-all");
      await fetchFeed();
    } finally { setLoading(false); }
  };

  const handleItemClick = async (n) => {
    try { await api.post(`/in-app-notifications/${n.id}/read`); } catch (e) { /* noop */ }
    setOpen(false);
    if (n.request_id) navigate(`/requests/${n.request_id}`);
    fetchFeed();
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundOnState(next);
    if (next) playNotificationChime();
  };

  const testSound = (e) => {
    e.stopPropagation();
    playNotificationChime();
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notif-bell-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className="relative w-8 h-8 flex items-center justify-center border border-black hover:bg-black hover:text-white transition-colors"
      >
        <Bell size={14} strokeWidth={2} />
        {unread > 0 && (
          <span
            data-testid="notif-unread-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-600 text-white font-mono text-[9px] font-bold flex items-center justify-center border border-black"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-dropdown"
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-white border border-slate-200 z-[60] shadow-2xl"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
            <div className="flex items-baseline gap-2 flex-1 min-w-0">
              <span className="font-semibold text-sm tracking-tight uppercase">NOTIFICATIONS</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 truncate">
                {unread > 0 ? `${unread} UNREAD` : "ALL READ"}
              </span>
            </div>
            <button
              data-testid="notif-sound-toggle"
              onClick={toggleSound}
              title={soundOn ? "Sound ON — click to mute" : "Sound OFF — click to enable"}
              aria-label={soundOn ? "Mute notification sound" : "Enable notification sound"}
              className={`w-7 h-7 flex items-center justify-center border transition-colors ${
                soundOn ? "border-olive text-olive hover:bg-olive hover:text-white" : "border-slate-300 text-slate-400 hover:border-black hover:text-black"
              }`}
            >
              {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
            </button>
            {soundOn && (
              <button
                data-testid="notif-sound-test"
                onClick={testSound}
                title="Play test sound"
                aria-label="Play test notification sound"
                className="font-mono text-[10px] uppercase tracking-widest font-bold text-slate-600 hover:text-black hover:underline"
              >
                TEST
              </button>
            )}
            {unread > 0 && (
              <button
                data-testid="notif-mark-all-read"
                onClick={markAllRead}
                disabled={loading}
                className="font-mono text-[10px] uppercase tracking-widest font-bold text-olive hover:underline disabled:opacity-40 flex items-center gap-1"
              >
                <Check size={11} /> READ ALL
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center font-mono text-[11px] uppercase tracking-widest text-slate-400">
                NO NOTIFICATIONS YET
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-200 hover:bg-slate-50 transition-colors ${
                    n.read ? "bg-white" : "bg-olive/5"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1 inline-block w-1.5 h-1.5 bg-olive rounded-full flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-0.5 flex items-center gap-1">
                        <span>{(n.kind || "").replace(/_/g, " ")}</span>
                        {n.emergency && <span className="px-1 bg-red-600 text-white font-bold">EMERGENCY</span>}
                        <span className="text-slate-300">·</span>
                        <span>{timeAgo(n.created_at)}</span>
                      </div>
                      <div className="font-mono text-xs text-black leading-snug break-words">
                        {n.message}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
